
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Settings, 
  Link as LinkIcon, 
  Globe,
  Key,
  Zap,
  RefreshCcw,
  CheckCircle,
  AlertCircle,
  PlusCircle,
  Calendar,
  MessageSquare,
  Database
} from 'lucide-react';

const HQIntegrations = () => {
  const [newApiKey, setNewApiKey] = useState('');
  
  const integrations = [
    {
      id: 1,
      name: "Go High Level",
      type: "CRM",
      status: "connected",
      lastSync: "10 minutes ago",
      apiKey: "••••••••••••5823",
      description: "Marketing automation and CRM integration"
    },
    {
      id: 2,
      name: "Stripe",
      type: "Payment",
      status: "connected",
      lastSync: "1 hour ago",
      apiKey: "••••••••••••8472",
      description: "Payment processing and subscription management"
    },
    {
      id: 3,
      name: "Calendly",
      type: "Scheduling",
      status: "connected",
      lastSync: "30 minutes ago",
      apiKey: "••••••••••••1639",
      description: "Appointment scheduling and calendar management"
    },
    {
      id: 4,
      name: "Twilio",
      type: "Communication",
      status: "issue",
      lastSync: "3 hours ago",
      apiKey: "••••••••••••2947",
      description: "SMS and voice communication platform"
    },
    {
      id: 5,
      name: "EHR System",
      type: "Medical",
      status: "pending",
      lastSync: "Never",
      apiKey: "Not Connected",
      description: "Electronic Health Records system integration"
    }
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'connected': return 'bg-emerald-500/20 text-emerald-200 border-emerald-500/30';
      case 'pending': return 'bg-amber-500/20 text-amber-200 border-amber-500/30';
      case 'issue': return 'bg-red-500/20 text-red-200 border-red-500/30';
      default: return 'bg-slate-500/20 text-slate-200 border-slate-500/30';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'connected': return <CheckCircle className="w-4 h-4" />;
      case 'pending': return <AlertCircle className="w-4 h-4" />;
      case 'issue': return <AlertCircle className="w-4 h-4" />;
      default: return <AlertCircle className="w-4 h-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'CRM': return <MessageSquare className="w-5 h-5" />;
      case 'Payment': return <Database className="w-5 h-5" />;
      case 'Scheduling': return <Calendar className="w-5 h-5" />;
      case 'Communication': return <MessageSquare className="w-5 h-5" />;
      case 'Medical': return <Database className="w-5 h-5" />;
      default: return <Globe className="w-5 h-5" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Integrations
              </h1>
              <p className="mt-1 text-amber-200">
                API keys, GHL sync status, EHR connections
              </p>
            </div>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white">
              <PlusCircle className="w-4 h-4 mr-2" />
              Add Integration
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Integration Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-emerald-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-emerald-500/20">
                    <CheckCircle className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-200">Connected</p>
                    <p className="text-2xl font-bold text-white">3</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-red-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-red-500/20">
                    <AlertCircle className="h-6 w-6 text-red-400" />
                  </div>
                  <div>
                    <p className="text-sm text-red-200">Issues</p>
                    <p className="text-2xl font-bold text-white">1</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-amber-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-amber-500/20">
                    <PlusCircle className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-200">Pending</p>
                    <p className="text-2xl font-bold text-white">1</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* API Key Management */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">API Key Management</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4">
                <Input
                  type="text"
                  placeholder="Enter new API key name or ID..."
                  value={newApiKey}
                  onChange={(e) => setNewApiKey(e.target.value)}
                  className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400 flex-1"
                />
                <Button className="bg-amber-600 hover:bg-amber-700">
                  <Key className="w-4 h-4 mr-2" />
                  Generate Key
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Integrations List */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Connected Systems</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {integrations.map((integration) => (
                  <div key={integration.id} className="p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center bg-amber-500/20">
                          {getTypeIcon(integration.type)}
                        </div>
                        <div>
                          <h3 className="text-white font-semibold flex items-center">
                            {integration.name}
                            <Badge className="ml-2">
                              {integration.type}
                            </Badge>
                          </h3>
                          <p className="text-slate-300 text-sm">{integration.description}</p>
                        </div>
                      </div>
                      <Badge className={getStatusColor(integration.status)}>
                        <span className="flex items-center">
                          {getStatusIcon(integration.status)}
                          <span className="ml-1">{integration.status}</span>
                        </span>
                      </Badge>
                    </div>
                    
                    <div className="mt-4 flex justify-between items-center text-sm">
                      <div className="flex gap-6 text-slate-400">
                        <span>API Key: {integration.apiKey}</span>
                        <span>Last Sync: {integration.lastSync}</span>
                      </div>
                      <div className="flex space-x-2">
                        <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                          <Settings className="w-4 h-4 mr-2" />
                          Configure
                        </Button>
                        <Button size="sm" className="bg-amber-600 hover:bg-amber-700">
                          <RefreshCcw className="w-4 h-4 mr-2" />
                          Sync Now
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Webhook Configuration */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Webhook Endpoints</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                  <div>
                    <h3 className="text-white font-semibold">Appointment Notifications</h3>
                    <p className="text-slate-400 text-sm break-all mt-1">https://api.juv.com/webhooks/appointments/callback</p>
                  </div>
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Copy URL
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                  <div>
                    <h3 className="text-white font-semibold">Payment Events</h3>
                    <p className="text-slate-400 text-sm break-all mt-1">https://api.juv.com/webhooks/payments/callback</p>
                  </div>
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Copy URL
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-4 bg-slate-700/30 rounded-lg border border-slate-600/30">
                  <div>
                    <h3 className="text-white font-semibold">CRM Events</h3>
                    <p className="text-slate-400 text-sm break-all mt-1">https://api.juv.com/webhooks/crm/callback</p>
                  </div>
                  <Button size="sm" variant="outline" className="border-slate-600 text-slate-300">
                    <LinkIcon className="w-4 h-4 mr-2" />
                    Copy URL
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default HQIntegrations;
